import React, { useCallback, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  CustomIconButton,
  ErrorButtonContained,
  PrimaryButtonContained,
} from "../../../components";
import {
  Visibility,
  Edit,
  DeleteForever,
  FileCopy,
  Stop,
  PlayArrow,
} from "../../../icons";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@material-ui/core";

function CampaignsActions({
  campaign,
  onDelete,
  onClone,
  onStop,
  testId,
  onResume,
}) {
  const { t } = useTranslation();
  const [stopDialog, setStopDialog] = useState(false);
  const [resumeDialog, setResumeDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [cloneDialog, setCloneDialog] = useState(false);
  const { _id, createdById, status, schedule } = campaign;
  const history = useHistory();

  const scheduleDate = new Date(schedule);
  const presentDate = new Date();
  const dateIsDue = scheduleDate > presentDate;
  const is10MinutesBeforeScheduledDate =
    scheduleDate - presentDate < 10 * 60 * 1000;

  const userId = useSelector((state) => state.user.id);
  const canEdit =
    (userId === createdById &&
      status !== "RUNNING" &&
      status !== "WAITING_FOR_APPROVAL" &&
      status !== "STOPPED" &&
      status !== "COMPLETED") ||
    dateIsDue;

  const stoppable = status === "RUNNING";
  const resumable = status === "STOPPED";
  const deletable = !(status === "COMPLETED" || status === "LAUNCHED");
  const EditIcon = canEdit ? Edit : Visibility;
  const _onDelete = useCallback(() => onDelete(campaign), [onDelete, campaign]);

  const _onClone = useCallback(async () => {
    await onClone(campaign);
    setCloneDialog(false);
  }, [onClone, campaign, setCloneDialog]);

  const _onStop = useCallback(async () => {
    await onStop(campaign);
    setStopDialog(false);
  }, [onStop, campaign, setStopDialog]);

  const _onResume = useCallback(async () => {
    await onResume(campaign);
    setResumeDialog(false);
  }, [onResume, campaign, setResumeDialog]);

  const redirectCondition = () => {
    if (canEdit && status === "RUNNING") setEditDialog(true);
    else history.push(`/campaigns/${_id}/view`);
  };

  return (
    <>
      <Tooltip title={canEdit ? t("Edit campaign") : t("View campaign")}>
        <CustomIconButton
          // data-test-id={`${testId}-action-edit`}
          // component={Link}
          to={canEdit ? `/campaigns/${_id}` : `/campaigns/${_id}/view`}
          size="small"
          color="primary"
          onClick={redirectCondition}
        >
          <EditIcon fontSize="small" />
        </CustomIconButton>
      </Tooltip>
      {deletable && (status === "STOPPED" || !is10MinutesBeforeScheduledDate) && (
        <Tooltip title={t("Delete campaign")}>
          <CustomIconButton
            data-test-id={`${testId}-action-delete`}
            size="small"
            color="error"
            onClick={_onDelete}
          >
            <DeleteForever fontSize="small" />
          </CustomIconButton>
        </Tooltip>
      )}
      <Tooltip title={t("Clone Campaign")}>
        <CustomIconButton
          data-test-id={`${testId}-action-clone`}
          size="small"
          onClick={() => setCloneDialog(true)}
          color="primary"
        >
          <FileCopy fontSize="small" />
        </CustomIconButton>
      </Tooltip>
      {stoppable && !is10MinutesBeforeScheduledDate && (
        <Tooltip title={t("Stop campaign")}>
          <CustomIconButton
            data-test-id={`${testId}-action-stop`}
            size="small"
            onClick={() => setStopDialog(true)}
          >
            <Stop fontSize="small" />
          </CustomIconButton>
        </Tooltip>
      )}
      {resumable && (
        <Tooltip title={t("Resume campaign")}>
          <CustomIconButton
            data-test-id={`${testId}-action-resume`}
            size="small"
            onClick={() => setResumeDialog(true)}
          >
            <PlayArrow fontSize="small" />
          </CustomIconButton>
        </Tooltip>
      )}
      <Dialog
        open={stopDialog}
        onClose={() => setStopDialog(false)}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <DialogTitle id="dialog-title">{t("Stop Campaign")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="dialog-description">
            {t(`Are you sure you want to stop this campaign? Paused messages will automatically 
              expire 72 hours after scheduled date. If you need to resume after that point, 
              instead clone the campaign and approve again. Then delete the original.`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ErrorButtonContained onClick={_onStop} size="large">
            {t("Yes")}
          </ErrorButtonContained>
          <PrimaryButtonContained
            onClick={() => setStopDialog(false)}
            size="large"
          >
            {t("No")}
          </PrimaryButtonContained>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cloneDialog}
        onClose={() => setCloneDialog(false)}
        aria-labelledby="clone-dialog-title"
        aria-describedby="clone-dialog-description"
      >
        <DialogTitle id="clone-dialog-title">{t("Clone Campaign")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="clone-dialog-description">
            {t("Are you sure you want to clone this campaign?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ErrorButtonContained onClick={_onClone} size="large">
            {t("Yes")}
          </ErrorButtonContained>
          <PrimaryButtonContained
            onClick={() => setCloneDialog(false)}
            size="large"
          >
            {t("No")}
          </PrimaryButtonContained>
        </DialogActions>
      </Dialog>
      <Dialog
        open={resumeDialog}
        onClose={() => setResumeDialog(false)}
        aria-labelledby="resume-dialog-title"
        aria-describedby="resume-dialog-description"
      >
        <DialogTitle id="resume-dialog-title">
          {t("Resume Campaign")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="dialog-description">
            {t("Are you sure you want to resume this campaign?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ErrorButtonContained onClick={_onResume} size="large">
            {t("Yes")}
          </ErrorButtonContained>
          <PrimaryButtonContained
            onClick={() => setResumeDialog(false)}
            size="large"
          >
            {t("No")}
          </PrimaryButtonContained>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        aria-labelledby="edit-dialog-title"
        aria-describedby="edit-dialog-description"
      >
        <DialogTitle id="edit-dialog-title">{t("Edit Campaign")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="edit-dialog-description">
            {t("Are you sure you want to edit this campaign?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ErrorButtonContained
            component={Link}
            to={`/campaigns/${_id}`}
            size="large"
          >
            {t("Yes")}
          </ErrorButtonContained>
          <PrimaryButtonContained
            onClick={() => setEditDialog(false)}
            size="large"
          >
            {t("No")}
          </PrimaryButtonContained>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(CampaignsActions);
